import { useEffect, useRef, useState } from "react";

// Illustrations use the real board's eight columns. AT has support; the
// falling C's column is open below it, making this an eligible hook position.
export const HOOK_EXAMPLE = [
  [2,1,"C","falling"], [2,2,"A","settled"], [2,3,"T","settled"],
  [3,2,"E","settled"], [3,3,"R","settled"], [4,2,"O","settled"], [4,3,"N","settled"],
];
const BUILD_EXAMPLE = [[1,4,"R","falling"],[4,1,"C","settled"],[4,2,"A","settled"],[4,3,"T","settled"],[2,7,"D","settled"],[3,7,"O","settled"],[4,7,"G","settled"]];
const REVEAL_EXAMPLE = [[4,1,"C","settled"],[4,2,"A","settled"],[4,3,"T","settled"]];

function BoardExample({ kind }) {
  const [phase, setPhase] = useState("ready");
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const source = kind === "hook" ? HOOK_EXAMPLE : kind === "build" ? BUILD_EXAMPLE : REVEAL_EXAMPLE;
  function demonstrate() {
    if (phase === "done") { setPhase("ready"); return; }
    if (phase !== "ready") return;
    setPhase("revealing");
    timer.current = setTimeout(() => setPhase("done"), 180);
  }
  return <div className={`example example-${kind}`} data-phase={phase}>
    <div className="example-board" role="img" aria-label={kind === "build" ? "Eight-column board detail: CAT across the bottom and DOG down the right. A red R is falling." : kind === "hook" ? "C is falling beside AT. AT sits on two supported columns. The space below C is empty, so Hook can complete CAT before C lands." : "CAT stays on the bottom row until the player drags across it and releases to reveal."}>
      {Array.from({ length:40 }, (_,i) => {
        const row = Math.floor(i/8), col = i%8;
        const entry = source.find(([r,c]) => r === row && c === col);
        const inWord = kind === "hook" ? row === 2 && col >= 1 && col <= 3 : kind === "reveal" && row === 4 && col >= 1 && col <= 3;
        const removed = inWord && phase === "done";
        const state = removed || !entry ? "empty" : inWord && phase === "revealing" ? "revealing" : entry[3];
        return <span key={i} className={`example-tile tile-${state}`} data-example-cell={`${row},${col}`}>{removed ? "" : entry?.[2] || ""}</span>;
      })}
      {kind === "reveal" && phase === "ready" && <span className="example-drag" aria-hidden="true">→</span>}
      {kind === "hook" && phase === "ready" && <span className="example-hook-arrow" aria-hidden="true">↦</span>}
    </div>
    {kind === "build" ? <div className="example-caption">ACROSS → <span>↓ DOWN</span></div> : <div className="example-action"><span role="status">{phase === "done" ? kind === "hook" ? "CAT hooked & scored" : "CAT revealed & scored" : kind === "hook" ? "Before C lands" : "Your word. Your choice."}</span><button onClick={demonstrate} disabled={phase === "revealing"}>{phase === "done" ? "Replay" : kind === "hook" ? "HOOK ↳" : "See reveal ↗"}</button></div>}
  </div>;
}

export default function VisualTutorial({ onHome }) {
  return <section className="visual-tutorial" aria-labelledby="tutorial-title">
    <div className="tutorial-heading"><div><span className="eyebrow">HOW TO PLAY</span><h1 id="tutorial-title">Build it. Then reveal it.</h1></div><button onClick={onHome}>HOME</button></div>
    <div className="tutorial-steps">
      <article className="tutorial-step"><div className="step-copy"><span className="step-number">01</span><h2>Drop & build</h2><p>Stack falling letters into words <b>across or down.</b> Completed words stay until you choose to reveal them.</p></div><BoardExample kind="build"/></article>
      <article className="tutorial-step"><div className="step-copy"><span className="step-number">02</span><h2>Choose your reveal</h2><p><b>Drag across a word</b> with your mouse or finger. Release to reveal it, score, and make room.</p></div><BoardExample kind="reveal"/></article>
      <article className="tutorial-step"><div className="step-copy"><span className="step-number">03</span><h2>Catch a word</h2><p>When a falling tile lines up beside a word, <b>hook before it lands.</b> Use W or scroll up; on touch, tap HOOK.</p></div><BoardExample kind="hook"/></article>
    </div>
    <p className="top-warning"><span aria-hidden="true">↑</span> Keep room to build. <b>Don’t let the stack reach the top.</b></p>
    <section className="tutorial-controls" aria-labelledby="control-map-title">
      <h2 id="control-map-title">Your controls</h2>
      <div className="control-sections">
        <section className="control-section" aria-labelledby="computer-controls-title">
          <h3 id="computer-controls-title">Keyboard & Mouse</h3>
          <dl>
            <div><dt>Move</dt><dd><p><kbd>A</kbd> / <kbd>D</kbd> or <kbd>←</kbd> / <kbd>→</kbd>, hold to repeat.</p><p>Mouse: click or hold ← / →.</p></dd></div>
            <div><dt>Faster fall</dt><dd><p>Hold <kbd>S</kbd> or <kbd>↓</kbd>.</p><p>Mouse: scroll down or hold <b>DROP</b>.</p></dd></div>
            <div><dt>Hook</dt><dd><p><kbd>W</kbd> or <kbd>↑</kbd>.</p><p>Mouse: scroll up or click <b>HOOK</b>.</p></dd></div>
            <div><dt>Reveal</dt><dd><p>Drag across a word and release.</p><p>Keyboard selection: <kbd>R</kbd>, arrows to position, <kbd>Space</kbd> to anchor, arrows to extend, <kbd>Enter</kbd> to reveal, <kbd>Esc</kbd> to cancel.</p></dd></div>
            <div><dt>Pause</dt><dd><b>HOME</b> pauses the game. Use <b>Continue</b> to return or <b>Save Game</b> to keep it for later.</dd></div>
          </dl>
        </section>
        <section className="control-section touchscreen-controls" aria-labelledby="touch-controls-title">
          <h3 id="touch-controls-title">Touchscreen</h3>
          <dl>
            <div><dt>Move</dt><dd>Tap or hold ← / →.</dd></div>
            <div><dt>Faster fall</dt><dd>Tap or hold <b>↓ DROP</b>.</dd></div>
            <div><dt>Hook</dt><dd>Tap <b>HOOK</b>.</dd></div>
            <div><dt>Reveal</dt><dd>Drag across a word, then lift your finger.</dd></div>
            <div><dt>Pause</dt><dd>Tap <b>HOME</b>, then use <b>Continue</b> or <b>Save Game</b>.</dd></div>
          </dl>
        </section>
      </div>
    </section>
  </section>;
}
